/**
 * Block Pack Leader checkout / IAP while Capacitor native OAuth or handoff is in flight.
 * SFVC has no Capacitor bridge — detectNativeIosForUpgrade() is false there, so Stripe
 * must be hard-blocked via OAuth flags, not platform detection alone.
 */

'use client'

import {
  CAPACITOR_HANDOFF_SESSION_PARAM,
  CAPACITOR_NATIVE_OAUTH_PARAM,
  CAPACITOR_NATIVE_OAUTH_VALUE,
  hasCapacitorOAuthBrowserSession,
  hasCapacitorOAuthFlowInBrowser,
  hasPendingCapacitorOAuthReturn,
  isCapacitorInAppBrowserContext,
  peekCapacitorHandoffSessionId,
} from '@/lib/auth/capacitorOAuthReturn'
import { isCapacitorHandoffInProgress } from '@/lib/auth/capacitorSessionHandoff'
import { isNativeCapacitorApp } from '@/lib/pwa/isNativeCapacitorApp'
import { logUpgradeFlow } from '@/lib/monetization/upgradeFlowDebugLog'

export const PENDING_UPGRADE_INTENT_KEY = 'wagamour_pending_upgrade_intent'

export type PendingUpgradeIntent = {
  feature: string
  source?: string
  valueProposition?: string
  savedAt: number
}

const OAUTH_AUTH_ROUTE_PREFIXES = [
  '/auth/complete',
  '/auth/capacitor-signin',
  '/api/auth/callback/',
  '/api/auth/capacitor-handoff/',
] as const

export const UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE =
  'Finish signing in and return to the app before upgrading.'

export function isNativeOAuthAuthRoute(pathname?: string): boolean {
  if (typeof window === 'undefined') return false
  const path = pathname ?? window.location.pathname
  return OAUTH_AUTH_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix))
}

export function getNativeOAuthBlockDebugSnapshot(): Record<string, unknown> {
  const handoffInProgress = isCapacitorHandoffInProgress()
  const pendingOAuthReturn = hasPendingCapacitorOAuthReturn()
  const capHsid = peekCapacitorHandoffSessionId()
  const oauthFlowInBrowser = hasCapacitorOAuthFlowInBrowser()
  const oauthBrowserSession = hasCapacitorOAuthBrowserSession()
  const inAppBrowserContext = isCapacitorInAppBrowserContext()
  const oauthAuthRoute = isNativeOAuthAuthRoute()

  const blocked =
    handoffInProgress ||
    pendingOAuthReturn ||
    Boolean(capHsid) ||
    oauthFlowInBrowser ||
    oauthBrowserSession ||
    inAppBrowserContext ||
    oauthAuthRoute

  return {
    blocked,
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    href: typeof window !== 'undefined' ? window.location.href : null,
    isNativeCapacitorApp: isNativeCapacitorApp(),
    handoffInProgress,
    pendingOAuthReturn,
    capHsid: capHsid?.slice(0, 8) ?? null,
    oauthFlowInBrowser,
    oauthBrowserSession,
    inAppBrowserContext,
    oauthAuthRoute,
  }
}

/**
 * Web/PWA: only block on explicit SFVC return URL markers or OAuth auth routes.
 * Do not treat sessionStorage leftovers alone as an in-app browser — those can
 * stick after a failed Cap handoff and silently no-op Free AI Fit → upgrade.
 */
function shouldBlockUpgradeOnWebOrPwa(): boolean {
  if (isNativeOAuthAuthRoute()) return true
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get(CAPACITOR_NATIVE_OAUTH_PARAM) === CAPACITOR_NATIVE_OAUTH_VALUE) {
      return true
    }
    if (params.get(CAPACITOR_HANDOFF_SESSION_PARAM)?.trim()) return true
  } catch {
    /* ignore */
  }
  return false
}

export function shouldBlockUpgradeDuringNativeOAuth(): boolean {
  if (typeof window === 'undefined') return false

  const snapshot = getNativeOAuthBlockDebugSnapshot()
  console.log('[UpgradeTrace9419bd7] shouldBlockUpgradeDuringNativeOAuth', snapshot)

  if (!isNativeCapacitorApp()) {
    return shouldBlockUpgradeOnWebOrPwa()
  }

  return snapshot.blocked === true
}

export function clearPendingUpgradeIntent(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(PENDING_UPGRADE_INTENT_KEY)
    localStorage.removeItem(PENDING_UPGRADE_INTENT_KEY)
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new CustomEvent('wagamour:upgradeIntentCleared'))
  } catch {
    // ignore
  }
}

/** Call when native OAuth browser opens — drop deferred upgrade attempts. */
export function clearUpgradeStateForNativeOAuthStart(): void {
  clearPendingUpgradeIntent()
  logUpgradeFlow('upgrade blocked — native OAuth starting', {
    isNativeCapacitorApp: isNativeCapacitorApp(),
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
  })
}

export function logUpgradeBlockedDuringNativeOAuth(context: string): void {
  logUpgradeFlow('blocked during native OAuth', {
    context,
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    isNativeCapacitorApp: isNativeCapacitorApp(),
    handoffInProgress: isCapacitorHandoffInProgress(),
    pendingOAuthReturn: hasPendingCapacitorOAuthReturn(),
    inAppBrowser: isCapacitorInAppBrowserContext(),
    oauthAuthRoute: isNativeOAuthAuthRoute(),
  })
}
